import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import { toast } from '@/shared/utils/toast'
import { authService } from '@/services'
import { getInitials, storage } from '@/shared'
import { ROLE_LABELS } from '@/shared'
import type { RoleName } from '@/types/role'

export function useAuth() {
  const queryClient = useQueryClient()

  const {
    data: user,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: ['auth', 'user'],
    queryFn: async () => {
      // On page refresh, in-memory token is lost.
      // Proactively refresh via httpOnly cookie before calling /auth/me
      if (!storage.getToken()) {
        try {
          const refreshResult = await authService.refresh()

          storage.setToken(refreshResult.access_token)
        } catch {
          // No valid session — will be caught by PrivateRoute redirect
          throw new Error('No active session')
        }
      }

      const response = await authService.me()

      return response.user
    },
    retry: (failureCount, err) => {
      const status = (err as { response?: { status?: number } })?.response?.status

      // Retry only on throttle (429) or server errors (5xx) — not on auth errors
      if (status === 429 || (status !== undefined && status >= 500)) {
        return failureCount < 2
      }

      return false
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    staleTime: 1000 * 60 * 5,
  })

  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.id, email: user.email })

      if (!user.active) {
        storage.clearAll()
        queryClient.clear()
        toast.error('Sua conta foi desativada', {
          description: 'Entre em contato com o administrador.',
        })
        window.location.href = '/login'
      }
    }
  }, [user, queryClient])

  const logoutMutation = useMutation({
    mutationFn: authService.logout,
    onSuccess: () => {
      Sentry.setUser(null)
queryClient.clear()
      window.location.href = '/login'
    },
  })

  const userName = user?.name ?? 'Usuário'
  const userEmail = user?.email ?? ''
  const userRoles = user?.roles.map((r) => r.name) ?? []
  const primaryRole = userRoles[0] ?? 'student'
  const userInitials = getInitials(userName)
  const userRoleLabel = ROLE_LABELS[primaryRole] ?? 'Aluno'

  return {
    user,
    userName,
    userEmail,
    userRoles,
    userRole: primaryRole,
    userInitials,
    userRoleLabel,
    isLoading: isPending,
    isAuthenticated: !!user && user.active,
    isError,
    error,
    logout: logoutMutation.mutate,
    isLoggingOut: logoutMutation.isPending,
  }
}

export function getRoleLabel(roleName: RoleName): string {
  return ROLE_LABELS[roleName]
}
